// File Location: src/services/fileUploadService.js

import { storage } from './firebaseConfig';
import { ref, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';

/**
 * Upload file to Firebase Storage
 * @param {File} file - The file to upload
 * @param {string} path - Storage path (e.g., 'disputes/dispute-id/filename')
 * @param {function} onProgress - Progress callback function
 * @returns {Promise<Object>} Upload result with download URL and metadata
 */
export const uploadFile = async (file, path, onProgress = null) => {
  try {
    // Validate file
    if (!file || !(file instanceof File)) {
      throw new Error('Invalid file provided');
    }

    // Check file size (max 100MB)
    const maxSize = 100 * 1024 * 1024; // 100MB
    if (file.size > maxSize) {
      throw new Error('File size exceeds 100MB limit');
    }

    // Check file type
    const allowedTypes = [
      'application/pdf',
      'application/msword',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      'image/jpeg',
      'image/jpg',
      'image/png',
      'text/plain'
    ];

    if (!allowedTypes.includes(file.type)) {
      throw new Error('File type not supported. Allowed: PDF, DOC, DOCX, JPG, PNG, TXT');
    }

    // Create storage reference
    const storageRef = ref(storage, path);

    // Upload file
    console.log('Uploading file to Firebase Storage:', path);
    const uploadResult = await uploadBytes(storageRef, file);

    // Get download URL
    const downloadURL = await getDownloadURL(uploadResult.ref);

    console.log('File uploaded successfully:', downloadURL);

    return {
      success: true,
      downloadURL,
      metadata: {
        name: file.name,
        size: file.size,
        type: file.type,
        lastModified: file.lastModified,
        fullPath: uploadResult.ref.fullPath,
        bucket: uploadResult.ref.bucket,
        uploadedAt: new Date().toISOString()
      }
    };

  } catch (error) {
    console.error('Error uploading file:', error);
    throw new Error(`Upload failed: ${error.message}`);
  }
};

/**
 * Delete file from Firebase Storage
 * @param {string} path - Storage path of the file to delete
 * @returns {Promise<boolean>} Success status
 */
export const deleteFile = async (path) => {
  try {
    const storageRef = ref(storage, path);
    await deleteObject(storageRef);
    console.log('File deleted successfully:', path);
    return true;
  } catch (error) {
    console.error('Error deleting file:', error);
    throw new Error(`Delete failed: ${error.message}`);
  }
};

/**
 * Generate unique file path for dispute attachments
 * @param {string} disputeId - Query ID
 * @param {string} queryIndex - Query index (optional)
 * @param {string} fileName - Original file name
 * @returns {string} Unique storage path
 */
export const generateDisputeFilePath = (disputeId, queryIndex, fileName) => {
  const timestamp = Date.now();
  const uniqueToken = Math.random().toString(36).slice(2, 8);
  const sanitizedFileName = fileName.replace(/[^a-zA-Z0-9.-]/g, '_');
  
  if (queryIndex !== undefined && queryIndex !== null) {
    return `disputes/${disputeId}/query_${queryIndex}/${timestamp}_${uniqueToken}_${sanitizedFileName}`;
  }
  
  return `disputes/${disputeId}/${timestamp}_${uniqueToken}_${sanitizedFileName}`;
};

/**
 * Upload multiple files with progress tracking
 * @param {Array} files - Array of {file, path} objects
 * @param {function} onProgress - Progress callback
 * @returns {Promise<Array>} Upload results
 */
export const uploadMultipleFiles = async (files, onProgress = null) => {
  try {
    const results = [];
    
    for (let i = 0; i < files.length; i++) {
      const { file, path } = files[i];
      
      if (onProgress) {
        onProgress({
          currentFile: i + 1,
          totalFiles: files.length,
          fileName: file.name,
          status: 'uploading'
        });
      }
      
      const result = await uploadFile(file, path);
      results.push(result);
      
      if (onProgress) {
        onProgress({
          currentFile: i + 1,
          totalFiles: files.length,
          fileName: file.name,
          status: 'completed'
        });
      }
    }
    
    return results;
  } catch (error) {
    console.error('Error uploading multiple files:', error);
    throw error;
  }
};

/**
 * Get file extension from file name
 * @param {string} fileName - File name
 * @returns {string} File extension
 */
export const getFileExtension = (fileName) => {
  return fileName.split('.').pop().toLowerCase();
};

/**
 * Format file size for display
 * @param {number} bytes - File size in bytes
 * @returns {string} Formatted file size
 */
export const formatFileSize = (bytes) => {
  if (bytes === 0) return '0 Bytes';
  
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
};
